/**
 * 航路：一座座岛按「航行距离」而不是「世界坐标」排列。
 *
 * 因为船几乎不动、是海和岛在动（§3.1），岛的唯一身份就是
 * 「船开到第几米时经过它、横向偏多少」。两端用同一个 seed 就能算出同一串岛，
 * 一个字节都不用同步。
 */

import { hash2, splitmix32 } from './rng.js';

export interface IslandSpec {
  /** 第几座岛（从 0 开始），也是它在本串里的唯一 id */
  readonly id: number;
  /** 船航行到多少米时，岛到达正舷侧 */
  readonly atDistance: number;
  /** 横向偏移（米），正数在右舷 */
  readonly lateral: number;
  /** 岛的水平半径（米） */
  readonly radius: number;
  /** 岛的高度（米） */
  readonly height: number;
  /** 生物群系 0..1（决定配色/植被） */
  readonly style: number;
  /** 绕 Y 轴的朝向（弧度） */
  readonly rotation: number;
  /** 岛上附加物的种类 0..1 */
  readonly feature: number;
}

export interface VoyageOptions {
  seed: number;
  /** 两岛之间的最小 / 最大航程间隙（米） */
  minGap?: number;
  maxGap?: number;
  /** 在「岛半径 + 净距」之外，再向外散开的范围（米） */
  lateralSpread?: number;
  /** 岛边缘到航线的最小净距（米）——防止岛长在船身上 */
  clearance?: number;
  /** 出现「只有海的一段」的概率 */
  quietChance?: number;
  /** 第一座岛出现的距离（米） */
  firstAt?: number;
}

const DEFAULTS = {
  minGap: 70,
  maxGap: 210,
  lateralSpread: 150,
  clearance: 30,
  quietChance: 0.16,
  firstAt: 140,
} as const;

/**
 * 一条无限延伸的航路。
 * 用法：每帧 `ensure(distance + horizon)`，然后 `visible(distance, back, ahead)` 取要渲染的岛。
 */
export class Voyage {
  readonly options: Required<VoyageOptions>;
  readonly islands: IslandSpec[] = [];

  private nextId = 0;
  private nextAt: number;

  constructor(options: VoyageOptions) {
    this.options = {
      minGap: options.minGap ?? DEFAULTS.minGap,
      maxGap: options.maxGap ?? DEFAULTS.maxGap,
      lateralSpread: options.lateralSpread ?? DEFAULTS.lateralSpread,
      clearance: options.clearance ?? DEFAULTS.clearance,
      quietChance: options.quietChance ?? DEFAULTS.quietChance,
      firstAt: options.firstAt ?? DEFAULTS.firstAt,
      seed: options.seed,
    };
    this.nextAt = this.options.firstAt;
  }

  /** 保证「航行距离 + 地平线」之内的岛都已生成。纯增量、幂等、可任意顺序调用。 */
  ensure(untilDistance: number): void {
    while (this.nextAt <= untilDistance) {
      this.islands.push(this.buildIsland(this.nextId++, this.nextAt));
      this.nextAt += this.gapAfter(this.islands.length - 1);
    }
  }

  /** 取当前可见的岛：z ∈ [-ahead, +back]（船头朝 -Z，所以前方是负数）。 */
  visible(distance: number, back = 60, ahead = 700): IslandSpec[] {
    const out: IslandSpec[] = [];
    for (let i = 0; i < this.islands.length; i++) {
      const island = this.islands[i]!;
      const z = distance - island.atDistance;
      if (z >= -ahead && z <= back) out.push(island);
    }
    return out;
  }

  /** 岛在「船局部坐标」里的 z（前方为负）。 */
  static zAt(island: IslandSpec, distance: number): number {
    return distance - island.atDistance;
  }

  private gapAfter(index: number): number {
    const rand = this.randFor(index * 2 + 1);
    const base = this.options.minGap + rand() * (this.options.maxGap - this.options.minGap);
    const quiet = rand() < this.options.quietChance;
    return quiet ? base * 2.6 : base;
  }

  private buildIsland(id: number, atDistance: number): IslandSpec {
    const rand = this.randFor(id * 2);
    const radius = 16 + rand() * 74;
    // 岛必须离航线有净距，否则会出现「船从岛里穿过去」——截图里那块棕色「甲板」就是这么来的
    const side = rand() < 0.5 ? -1 : 1;
    const lateral = side * (radius + this.options.clearance + rand() * this.options.lateralSpread);
    return {
      id,
      atDistance,
      lateral,
      radius,
      height: radius * (0.25 + rand() * 0.55),
      style: rand(),
      rotation: rand() * Math.PI * 2,
      feature: rand(),
    };
  }

  private randFor(salt: number): () => number {
    return splitmix32(hash2(this.options.seed, salt));
  }
}
